import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useInput, useNotify } from 'react-admin';

const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

export type GstStateChoice = {
    id: number;
    name: string;
    state_code: string;
    tin_number: string;
};

export type GstRow = {
    state_id: number;
    gst_number: string;
    gst_registration_year?: number;
    is_primary?: boolean;
    state_name?: string;
};

const toPayload = (rows: GstRow[]): GstRow[] =>
    rows.map((row) => ({
        state_id: row.state_id,
        gst_number: row.gst_number.trim().toUpperCase(),
        gst_registration_year: row.gst_registration_year ?? undefined,
        is_primary: Boolean(row.is_primary),
    }));

const payloadsEqual = (left: GstRow[], right: GstRow[]) => {
    const normalize = (rows: GstRow[]) =>
        toPayload(rows).sort((a, b) => a.state_id - b.state_id);
    const a = normalize(left);
    const b = normalize(right);
    if (a.length !== b.length) {
        return false;
    }
    return a.every((row, index) => {
        const other = b[index];
        return (
            row.state_id === other.state_id &&
            row.gst_number === other.gst_number &&
            row.gst_registration_year === other.gst_registration_year &&
            Boolean(row.is_primary) === Boolean(other.is_primary)
        );
    });
};

type GstManagerProps = {
    states: GstStateChoice[];
    gstsSource?: string;
    managedSource?: string;
    dialogTitle?: string;
};

export const GstManager: React.FC<GstManagerProps> = ({
    states,
    gstsSource = 'gsts',
    managedSource = 'gsts_managed',
    dialogTitle = 'Manage GST registrations',
}) => {
    const notify = useNotify();
    const { field } = useInput({ source: gstsSource });
    const { field: managedField } = useInput({ source: managedSource, defaultValue: false });
    const [open, setOpen] = useState(false);
    const [draftRows, setDraftRows] = useState<GstRow[]>([]);
    const [stateId, setStateId] = useState<number | ''>('');
    const [gstNumber, setGstNumber] = useState('');
    const [regYear, setRegYear] = useState('');

    const currentRows = useMemo(
        () => (Array.isArray(field.value) ? field.value as GstRow[] : []),
        [field.value]
    );

    const stateById = useMemo(
        () => new Map(states.map((state) => [state.id, state])),
        [states]
    );

    const openDialog = () => {
        setDraftRows(
            currentRows.map((row) => ({
                state_id: row.state_id,
                gst_number: row.gst_number,
                gst_registration_year: row.gst_registration_year,
                is_primary: Boolean(row.is_primary),
                state_name: row.state_name || stateById.get(row.state_id)?.name,
            }))
        );
        setStateId('');
        setGstNumber('');
        setRegYear('');
        setOpen(true);
    };

    const closeDialog = () => setOpen(false);

    const validateDraftRow = (row: { state_id: number; gst_number: string; gst_registration_year?: number }) => {
        const normalized = row.gst_number.trim().toUpperCase();
        if (!GST_REGEX.test(normalized)) {
            throw new Error('Invalid GST number format');
        }
        const state = stateById.get(row.state_id);
        if (!state) {
            throw new Error('Select a valid state');
        }
        const tin = String(state.tin_number || '').padStart(2, '0');
        if (normalized.substring(0, 2) !== tin) {
            throw new Error(`GST prefix must match ${state.name} (${tin})`);
        }
        if (draftRows.some((existing) => existing.state_id === row.state_id)) {
            throw new Error('A GST for this state is already added');
        }
        if (draftRows.some((existing) => existing.gst_number.toUpperCase() === normalized)) {
            throw new Error('This GST number is already in the list');
        }
        if (row.gst_registration_year != null) {
            const year = Number(row.gst_registration_year);
            if (!Number.isInteger(year) || year < 1900 || year > 9999) {
                throw new Error('Invalid GST registration year');
            }
        }
        return normalized;
    };

    const handleAdd = () => {
        if (!stateId) {
            notify('Select a state', { type: 'warning' });
            return;
        }
        try {
            const normalized = validateDraftRow({
                state_id: Number(stateId),
                gst_number: gstNumber,
                gst_registration_year: regYear ? Number(regYear) : undefined,
            });
            const state = stateById.get(Number(stateId));
            const nextRow: GstRow = {
                state_id: Number(stateId),
                gst_number: normalized,
                gst_registration_year: regYear ? Number(regYear) : undefined,
                is_primary: draftRows.length === 0,
                state_name: state?.name,
            };
            setDraftRows((rows) => [...rows, nextRow]);
            setStateId('');
            setGstNumber('');
            setRegYear('');
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Could not add GST', { type: 'warning' });
        }
    };

    const handleRemove = (stateIdToRemove: number) => {
        setDraftRows((rows) => {
            const filtered = rows.filter((row) => row.state_id !== stateIdToRemove);
            if (filtered.length > 0 && !filtered.some((row) => row.is_primary)) {
                filtered[0].is_primary = true;
            }
            return filtered;
        });
    };

    const handleSetPrimary = (stateIdToSet: number) => {
        setDraftRows((rows) =>
            rows.map((row) => ({
                ...row,
                is_primary: row.state_id === stateIdToSet,
            }))
        );
    };

    const handleSave = () => {
        const nextPayload = toPayload(draftRows);
        field.onChange(nextPayload);
        managedField.onChange(!payloadsEqual(currentRows, draftRows));
        closeDialog();
    };

    const count = currentRows.length;

    const resolveStateName = (row: GstRow) =>
        row.state_name || stateById.get(row.state_id)?.name || '—';

    return (
        <Box sx={{ width: '100%' }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">GST registrations</Typography>
                <Button variant="outlined" size="small" onClick={openDialog}>
                    Manage GSTs
                </Button>
            </Stack>

            {count === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No GST registrations yet.
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small" aria-label="GST registrations">
                        <TableHead>
                            <TableRow>
                                <TableCell>State</TableCell>
                                <TableCell>GST number</TableCell>
                                <TableCell>Registration year</TableCell>
                                <TableCell>Primary</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {currentRows.map((row) => (
                                <TableRow key={row.state_id}>
                                    <TableCell>{resolveStateName(row)}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{row.gst_number}</TableCell>
                                    <TableCell>{row.gst_registration_year ?? '—'}</TableCell>
                                    <TableCell>
                                        {row.is_primary ? (
                                            <Chip label="Primary" size="small" color="primary" variant="outlined" />
                                        ) : (
                                            '—'
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="md">
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <FormControl fullWidth>
                                <InputLabel id="gst-manager-state-label">State</InputLabel>
                                <Select
                                    labelId="gst-manager-state-label"
                                    label="State"
                                    value={stateId}
                                    onChange={(event) => setStateId(event.target.value as number | '')}
                                >
                                    {states.map((state) => (
                                        <MenuItem key={state.id} value={state.id}>
                                            {state.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="GST number"
                                value={gstNumber}
                                onChange={(event) => setGstNumber(event.target.value.toUpperCase())}
                                fullWidth
                            />
                            <TextField
                                label="Reg. year"
                                value={regYear}
                                onChange={(event) => setRegYear(event.target.value.replace(/\D/g, '').slice(0, 4))}
                                sx={{ minWidth: 120 }}
                            />
                            <Button variant="contained" onClick={handleAdd} sx={{ whiteSpace: 'nowrap' }}>
                                Add GST
                            </Button>
                        </Stack>

                        {draftRows.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No GST registrations yet. Select a state and enter a GST number.
                            </Typography>
                        ) : (
                            <Stack spacing={1}>
                                {draftRows.map((row) => (
                                    <Stack
                                        key={row.state_id}
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            p: 1,
                                        }}
                                    >
                                        <Typography sx={{ minWidth: 120, fontWeight: 500 }}>
                                            {row.state_name || stateById.get(row.state_id)?.name}
                                        </Typography>
                                        <Typography sx={{ flex: 1, fontFamily: 'monospace' }}>
                                            {row.gst_number}
                                        </Typography>
                                        {row.gst_registration_year ? (
                                            <Typography variant="body2" color="text.secondary">
                                                {row.gst_registration_year}
                                            </Typography>
                                        ) : null}
                                        {row.is_primary ? (
                                            <Typography variant="caption" color="primary">
                                                Primary
                                            </Typography>
                                        ) : (
                                            <Button size="small" onClick={() => handleSetPrimary(row.state_id)}>
                                                Set primary
                                            </Button>
                                        )}
                                        <IconButton
                                            aria-label="Remove GST"
                                            onClick={() => handleRemove(row.state_id)}
                                            size="small"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
